// 개발요청 — 부서 순위 정렬 창(D4b). 열린 회차의 내 부서 요청을 «↑»·«↓» 로 옮기고 «저장» → PUT /api/devreq/requests/rank.
// 🔴 order 에는 그 (회차, 부서)의 철회 안 된 요청을 이월 건까지 전부 담는다(서버 rank_requests 가 빠짐없이 요구한다).
// 끌어 놓기는 넣지 않는다 — 휴대폰에서도 같은 조작이 되게. 막는 것은 서버다(409 문구를 창 안에 보인다).
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import { errorText, statusBadge, text } from './devreqLabels';

const LOAD_FAILED = '요청 목록을 불러오지 못했습니다.';
const SAVE_FAILED = '순위를 저장하지 못했습니다.';
const CARRIED_BADGE = {
  display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
  background: COLORS.warnSoft, color: COLORS.warn, whiteSpace: 'nowrap',
};

// 지금 순위대로 — 미정은 뒤, 같으면 id.
const byRank = (a, b) => {
  const ar = a.dept_rank === null || a.dept_rank === undefined;
  const br = b.dept_rank === null || b.dept_rank === undefined;
  if (ar !== br) return ar ? 1 : -1;
  if (!ar && a.dept_rank !== b.dept_rank) return a.dept_rank - b.dept_rank;
  return a.id - b.id;
};

export default function DevRequestRankPanel({ me, round, onClose, onSaved }) {
  const myDepts = Array.isArray(me?.departments) ? me.departments : [];
  const [deptChoice, setDeptChoice] = useState(() => String(myDepts[0]?.id ?? ''));
  const [rows, setRows] = useState(null);   // null = 불러오는 중
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const deptId = Number(deptChoice) || null;
  const roundId = round?.id ?? null;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setRows(null);
    setLoadError('');
    setError('');
    if (roundId === null || !deptId) return undefined;
    let alive = true;
    devreqAPI.list({ round_id: roundId, dept_id: deptId })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r?.data?.items) ? r.data.items : [];
        // 이월 건도 이 회차의 행이라 그대로 담는다 — 철회된 건만 뺀다.
        setRows(list.filter((it) => it.dept_id === deptId && it.round_id === roundId && !it.withdrawn).sort(byRank));
      })
      .catch(async (err) => {
        const message = await errorText(err, LOAD_FAILED);
        if (alive) setLoadError(message);
      });
    return () => { alive = false; };
  }, [roundId, deptId]);

  const move = (index, delta) => {
    setRows((prev) => {
      const next = [...(prev || [])];
      const to = index + delta;
      if (to < 0 || to >= next.length) return prev;
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const save = async () => {
    if (savingRef.current || !rows || rows.length === 0) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const r = await devreqAPI.rank({ round_id: roundId, dept_id: deptId, order: rows.map((it) => it.id) });
      if (mountedRef.current) onSaved?.(r?.data || null);
    } catch (err) {
      const message = await errorText(err, SAVE_FAILED);
      if (mountedRef.current) setError(message);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const requestClose = () => {
    if (savingRef.current) return;
    onClose?.();
  };

  const canSave = !!rows && rows.length > 0 && !saving;

  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="부서 순위 정렬"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            requestClose();
          }
        }}
        style={{
          width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
          padding: '22px', borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '18px' }}>부서 순위 정렬</h2>

        {myDepts.length >= 2 ? (
          <select aria-label="순위를 정할 부서" value={deptChoice} onChange={(e) => setDeptChoice(e.target.value)}
            style={{ ...inputStyle, marginBottom: '12px' }}>
            {myDepts.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
          </select>
        ) : (
          <div style={{ marginBottom: '12px', fontSize: '14px', color: COLORS.text }}>{myDepts[0]?.name || '—'}</div>
        )}

        {loadError && <div role="alert" style={{ marginBottom: '12px', color: COLORS.dangerText, fontSize: '13px' }}>{loadError}</div>}
        {!loadError && rows === null && <div role="status" style={{ color: COLORS.textMuted, fontSize: '13px', marginBottom: '12px' }}>불러오는 중…</div>}
        {rows && rows.length === 0 && (
          <div style={{ color: COLORS.textMuted, fontSize: '13px', marginBottom: '12px' }}>이 부서의 요청이 없습니다</div>
        )}

        {rows && rows.length > 0 && (
          <ol aria-label="순위 목록" style={{ listStyle: 'none', margin: '0 0 12px', padding: 0 }}>
            {rows.map((it, index) => (
              <li key={it.id} data-testid="devreq-rank-row"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, minWidth: 0 }}>
                <span style={{ width: '24px', textAlign: 'right', fontWeight: 700, color: COLORS.textSub, flexShrink: 0 }}>{index + 1}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: '13px', overflowWrap: 'anywhere' }}>
                  <span style={{ fontWeight: 700 }}>{text(it.req_no)}</span>{' '}
                  {it.carried && <><span style={CARRIED_BADGE}>{`이월(${text(it.carried_from_round_no)}회차)`}</span>{' '}</>}
                  {it.status && <><span style={statusBadge(it.status)}>{it.status}</span>{' '}</>}
                  <span>{text(it.title)}</span>
                </span>
                <button type="button" aria-label={`${it.req_no} 위로`} disabled={index === 0 || saving}
                  onClick={() => move(index, -1)} style={{ ...btn('secondary', 'sm'), opacity: index === 0 ? 0.4 : 1 }}>↑</button>
                <button type="button" aria-label={`${it.req_no} 아래로`} disabled={index === rows.length - 1 || saving}
                  onClick={() => move(index, 1)} style={{ ...btn('secondary', 'sm'), opacity: index === rows.length - 1 ? 0.4 : 1 }}>↓</button>
              </li>
            ))}
          </ol>
        )}

        {error && (
          <div role="alert" style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={requestClose} disabled={saving} style={btn('secondary')}>닫기</button>
          <button type="button" onClick={save} disabled={!canSave} style={{ ...btn('primary'), opacity: canSave ? 1 : 0.5 }}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
